import { store } from '../../state/patientProductionStore';

export interface CareAlert {
  type: 'lab' | 'followup' | 'prescription' | 'emergency';
  title: string;
  severity: 'low' | 'medium' | 'high';
  route: string;
}

export interface CareIntelligence {
  priorityServices: string[];
  alerts: CareAlert[];
}

const SERVICE_ROUTES: Record<string, string> = {
  doctor: '/patient/care-services/doctors',
  lab: '/patient/care-services/labs',
  imaging: '/patient/care-services/imaging',
  pharmacy: '/patient/care-services/pharmacy',
  hospital: '/patient/care-services/hospitals',
  telemedicine: '/patient/care-services/telemedicine',
  ambulance: '/patient/care-services/ambulance',
  insurance: '/patient/care-services/insurance',
};

const DEFAULT_ORDER = [
  '/patient/care-services/doctors',
  '/patient/care-services/labs',
  '/patient/care-services/imaging',
  '/patient/care-services/pharmacy',
  '/patient/care-services/hospitals',
  '/patient/care-services/telemedicine',
  '/patient/care-services/ambulance',
  '/patient/care-services/insurance',
];

export function getCareIntelligence(): CareIntelligence {
  try {
    const state = store.getState();
    const alerts: CareAlert[] = [];
    const priorityRoutes: string[] = [];

    const pendingLabs = state.labOrders.filter(o => o.status === 'BOOKED' || o.status === 'SAMPLE_COLLECTED' || o.status === 'PROCESSING');
    const pendingImaging = state.imagingOrders.filter(o => o.status === 'BOOKED');
    const upcomingAppts = state.appointments.filter(a => a.status === 'BOOKED' && new Date(a.date) >= new Date());
    const activePrescriptions = state.prescriptions;
    const hasEmergencyFlag = state.accessLogs.some(l => l.action === 'EMERGENCY_ACCESS');

    if (hasEmergencyFlag) {
      alerts.push({
        type: 'emergency',
        title: 'Recent emergency access detected — review your emergency contacts',
        severity: 'high',
        route: '/patient/access-logs',
      });
      priorityRoutes.push(SERVICE_ROUTES.ambulance);
    }

    if (pendingLabs.length > 0) {
      alerts.push({
        type: 'lab',
        title: `${pendingLabs.length} lab report${pendingLabs.length > 1 ? 's' : ''} pending — ${pendingLabs[0].testName}`,
        severity: 'medium',
        route: '/patient/lab-reports',
      });
      priorityRoutes.push(SERVICE_ROUTES.lab);
    }

    if (upcomingAppts.length > 0) {
      const next = upcomingAppts.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
      alerts.push({
        type: 'followup',
        title: `Upcoming: ${next.doctorName} — ${next.specialty} on ${next.date}`,
        severity: 'medium',
        route: '/patient/appointments',
      });
      priorityRoutes.push(SERVICE_ROUTES.doctor);
    }

    if (activePrescriptions.length > 0) {
      alerts.push({
        type: 'prescription',
        title: `${activePrescriptions.length} active prescription${activePrescriptions.length > 1 ? 's' : ''} — refill or reorder`,
        severity: 'low',
        route: '/patient/prescriptions',
      });
      priorityRoutes.push(SERVICE_ROUTES.pharmacy);
    }

    if (pendingImaging.length > 0) {
      alerts.push({
        type: 'lab',
        title: `${pendingImaging.length} imaging scan${pendingImaging.length > 1 ? 's' : ''} pending`,
        severity: 'medium',
        route: '/patient/imaging-reports',
      });
      priorityRoutes.push(SERVICE_ROUTES.imaging);
    }

    const seen = new Set(priorityRoutes);
    const priorityServices = [...priorityRoutes];
    for (const route of DEFAULT_ORDER) {
      if (!seen.has(route)) {
        priorityServices.push(route);
        seen.add(route);
      }
    }

    return { priorityServices, alerts };
  } catch {
    return { priorityServices: DEFAULT_ORDER, alerts: [] };
  }
}
